import React, { useCallback, useRef } from "react";
import { Table } from "antd";
import type { TableProps } from "antd";
import { PaginationFooter } from "./PaginationFooter";

export interface VirtualTableProps<T> extends TableProps<T> {
  /*基础参数*/
  loading: boolean;
  total: number;
  current: number;
  pageSize: number;
  cachedPages: number[];
  dataSource: T[];
  onChangePage: (page: number) => void;
  /*自动加载*/
  autoLoading: boolean;
  loadingProgress: {
    percentage: number;
    currentPage: number;
    totalPages: number;
  };
  onLoadNextPage?: () => void;
  onStartAutoLoad?: (loadNextPages: number[]) => void;
  onStopLoading?: () => void;
}

type DocgenInfo = {
  description?: string;
  displayName?: string;
  methods?: {
    name: string;
    description?: string;
    params?: {
      name: string;
      type: string;
      description?: string;
    }[];
    returns?: { type: string; description?: string };
  }[];
  props?: {
    [key: string]: {
      defaultValue?: unknown;
      description?: string;
      name: string;
      required?: boolean;
      type: { name: string; raw: string };
    };
  };
};

type ComponentWithDocgen<T> = React.FC<VirtualTableProps<T>> & {
  __docgenInfo?: DocgenInfo;
};

export const VirtualTable = <T,>(props: VirtualTableProps<T>) => {
  const {
    loading,
    autoLoading,
    loadingProgress,
    columns = [],
    dataSource = [],
    total,
    current,
    pageSize,
    cachedPages,
    onChangePage,
    onLoadNextPage,
    onStartAutoLoad,
    onStopLoading,
    ...restProps
  } = props;
  // antd Table 的 ref 类型来自 rc-table（pnpm 下不建议直接 import 传递依赖）。
  // 这里用结构化类型最小化定义，满足 scrollTo 能力即可。
  type AntdTableRef = {
    nativeElement: HTMLDivElement;
    scrollTo: (config: unknown) => void;
  };
  const tableRef = useRef<AntdTableRef | null>(null);

  const handleChangePage = useCallback(
    (page: number) => {
      onChangePage(page);
      if (!cachedPages.includes(page)) return;
      tableRef.current?.scrollTo({
        key: `${(page - 1) * pageSize + 1}`,
      });
    },
    [cachedPages, onChangePage, pageSize]
  );

  return (
    <div className="flex flex-col">
      <Table
        ref={tableRef}
        {...restProps}
        columns={columns}
        dataSource={dataSource}
        pagination={false}
        virtual
      />
      <PaginationFooter
        loading={loading}
        autoLoading={autoLoading}
        loadingProgress={loadingProgress}
        current={current}
        pageSize={pageSize}
        total={total}
        loadedCount={dataSource.length}
        cachedPages={cachedPages}
        onChange={handleChangePage}
        onLoadNextPage={onLoadNextPage}
        onStartAutoLoad={onStartAutoLoad}
        onStopLoading={onStopLoading}
      />
    </div>
  );
};

// VirtualTable组件的DocgenInfo，确保类型与默认值正确显示
(VirtualTable as ComponentWithDocgen<unknown>).__docgenInfo = {
  displayName: "VirtualTable",
  description: "虚拟滚动表格组件，支持分页、数据缓存和自动加载功能。",
  props: {
    // 基础参数
    loading: {
      description: "加载状态",
      name: "loading",
      required: true,
      type: { name: "boolean", raw: "boolean" },
      defaultValue: { value: "false" },
    },
    total: {
      description: "总数据条数",
      name: "total",
      required: true,
      type: { name: "number", raw: "number" },
      defaultValue: { value: "0" },
    },
    current: {
      description: "当前页码",
      name: "current",
      required: true,
      type: { name: "number", raw: "number" },
      defaultValue: { value: "1" },
    },
    pageSize: {
      description: "每页数据条数",
      name: "pageSize",
      required: true,
      type: { name: "number", raw: "number" },
      defaultValue: { value: "10" },
    },
    cachedPages: {
      description: "已缓存的页码数组",
      name: "cachedPages",
      required: true,
      type: { name: "number[]", raw: "number[]" },
      defaultValue: { value: "[]" },
    },
    dataSource: {
      description: "数据源",
      name: "dataSource",
      required: true,
      type: { name: "Array<T>", raw: "Array<T>" },
      defaultValue: { value: "[]" },
    },
    onChangePage: {
      description: "页码变更回调函数",
      name: "onChangePage",
      required: true,
      type: { name: "function", raw: "function(page: number): void" },
      defaultValue: { value: "-" },
    },
    // 自动加载相关
    autoLoading: {
      description: "是否正在自动加载",
      name: "autoLoading",
      required: true,
      type: { name: "boolean", raw: "boolean" },
      defaultValue: { value: "false" },
    },
    loadingProgress: {
      description: "加载进度信息",
      name: "loadingProgress",
      required: true,
      type: {
        name: "object",
        raw: "{percentage: 0, currentPage: 1, totalPages: 5}",
      },
      defaultValue: {
        value: "-",
      },
    },
    onLoadNextPage: {
      description: "加载下一页回调函数",
      name: "onLoadNextPage",
      required: false,
      type: { name: "function", raw: "function(page: number): void" },
      defaultValue: { value: "-" },
    },
    onStartAutoLoad: {
      description: "开始自动加载回调函数",
      name: "onStartAutoLoad",
      required: false,
      type: {
        name: "function",
        raw: "function(loadNextPages: number[]): void",
      },
      defaultValue: { value: "-" },
    },
    onStopLoading: {
      description: "停止加载回调函数",
      name: "onStopLoading",
      required: false,
      type: { name: "function", raw: "function(): void" },
      defaultValue: { value: "-" },
    },
    columns: {
      description: "表格列配置",
      name: "columns",
      required: true,
      type: { name: "Array<object>", raw: "Array<object>" },
      defaultValue: { value: "[]" },
    },
  },
};

// 添加默认导出
export default VirtualTable;
